import React, { useEffect, useState } from "react";
import { Plug, CheckCircle2, AlertCircle, Video, Mail, Calendar, ExternalLink } from "lucide-react";
import { adminIntegrations } from "../../lib/api";

export default function DashboardIntegrations() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminIntegrations();
        setState(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const g = state?.google_workspace;

  return (
    <div className="space-y-6">
      <div>
        <div className="kicker">Configuration</div>
        <h1 className="mt-2 font-display text-3xl font-bold text-white">Integrations</h1>
        <p className="mt-2 text-slate-400 max-w-2xl">Connect FortressPoint to your operational tools. Once connected, bookings automatically create Google Calendar events with Google Meet links, and contact submissions send notifications to <span className="text-cyan-300">support@fortresspointitsolutions.com</span>.</p>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-8 text-center text-sm text-slate-400">Loading...</div>
      ) : (
        <div className="glass rounded-2xl p-8">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                <Plug className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-white">Google Workspace</h2>
                <div className="mt-1 text-sm text-slate-400">Calendar · Meet · Gmail</div>
                <div className="mt-3 flex items-center gap-2">
                  {g?.connected ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 uppercase tracking-widest">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200 uppercase tracking-widest">
                      <AlertCircle className="w-3.5 h-3.5" /> Awaiting credentials
                    </span>
                  )}
                  {g?.credentials_configured ? (
                    <span className="text-[11px] text-slate-500">Credentials configured</span>
                  ) : (
                    <span className="text-[11px] text-slate-500">Credentials not yet added</span>
                  )}
                </div>
              </div>
            </div>
            <button
              disabled
              title="Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to enable"
              className="h-11 px-5 rounded-xl font-semibold border border-cyan-400/25 text-slate-300 bg-white/[0.03] cursor-not-allowed opacity-70 inline-flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Connect Google Workspace
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <FeatureBox icon={Calendar} title="Calendar Events" desc="Bookings auto-create events on support@fortresspointitsolutions.com's calendar." />
            <FeatureBox icon={Video} title="Google Meet Links" desc="Every event includes an auto-generated Google Meet conference link." />
            <FeatureBox icon={Mail} title="Gmail Notifications" desc="Contact submissions notify support@ and booking invites go to attendees." />
          </div>

          <div className="mt-8 p-5 rounded-xl bg-cyan-500/5 border border-cyan-400/15 text-sm text-slate-300">
            <div className="font-semibold text-cyan-200 mb-1">How to enable</div>
            <p className="text-slate-400 leading-relaxed">
              Provide <code className="px-1.5 py-0.5 rounded bg-white/10 text-cyan-200">GOOGLE_CLIENT_ID</code> and <code className="px-1.5 py-0.5 rounded bg-white/10 text-cyan-200">GOOGLE_CLIENT_SECRET</code> from Google Cloud Console (OAuth Web Client). Once added and the backend is restarted, this button becomes active and you can sign in as <span className="text-cyan-300">support@fortresspointitsolutions.com</span> to authorize Calendar + Gmail scopes.
            </p>
          </div>

          {g?.status_note && (
            <div className="mt-4 text-xs text-slate-500">{g.status_note}</div>
          )}
        </div>
      )}
    </div>
  );
}

function FeatureBox({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl p-4 border border-white/5 bg-white/[0.02]">
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500/20 to-cyan-400/20 border border-cyan-400/25 flex items-center justify-center mb-3">
        <Icon className="w-4 h-4 text-cyan-300" />
      </div>
      <div className="font-semibold text-white text-sm">{title}</div>
      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>
    </div>
  );
}
